import React, { useEffect, useRef } from "react";

/**
 * Aparición al hacer scroll.
 *
 * Usa IntersectionObserver y clases CSS en lugar de un motor de animación por
 * elemento: con la cantidad de bloques que anima esta página, eso es bastante
 * menos JavaScript en el hilo principal.
 *
 * Regla importante: el estado por defecto del elemento es VISIBLE. Solo se
 * oculta cuando este componente ya montó y confirmó que puede animarlo. Así,
 * si el JS no llega a ejecutarse, el contenido se ve igual.
 */

/**
 * Red de seguridad.
 *
 * Ocultar contenido a la espera de un observer es cómodo hasta que el
 * observer no entrega (pestaña en segundo plano, navegadores que congelan el
 * render, extensiones). En ese escenario la página quedaría casi entera en
 * blanco, que es un fallo mucho peor que perder una animación.
 *
 * IntersectionObserver entrega siempre una primera notificación al empezar a
 * observar. Si esa notificación no llega, damos el mecanismo por caído y
 * mostramos todo.
 */
const FAILSAFE_MS = 1500;
let observerConfirmed = false;
let failsafeTimer = null;

const revealEverything = () => {
  document
    .querySelectorAll("[data-reveal]")
    .forEach((node) => node.classList.add("reveal-in"));
};

const armFailsafe = () => {
  if (observerConfirmed || failsafeTimer) return;
  failsafeTimer = window.setTimeout(() => {
    if (!observerConfirmed) revealEverything();
  }, FAILSAFE_MS);
};

const confirmObserver = () => {
  if (observerConfirmed) return;
  observerConfirmed = true;
  if (failsafeTimer) {
    window.clearTimeout(failsafeTimer);
    failsafeTimer = null;
  }
};

const Reveal = ({
  children,
  as: Tag = "div",
  delay = 0,
  y = 24,
  amount = 0.25,
  className = "",
  ...rest
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      node.classList.add("reveal-in");
      return undefined;
    }

    node.classList.add("reveal-armed");
    armFailsafe();

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Llegó una notificación: el mecanismo funciona.
        confirmObserver();
        if (!entry.isIntersecting) return;
        node.classList.add("reveal-in");
        observer.disconnect();
      },
      { threshold: amount, rootMargin: "0px 0px -5% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [amount]);

  return (
    <Tag
      ref={ref}
      data-reveal=""
      className={className}
      style={{ "--reveal-delay": `${delay}s`, "--reveal-y": `${y}px` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
